import { useEffect } from 'react';
import { toast } from '../../components/ui/Toast';
import { useNavigate } from 'react-router-dom';
import {
  calculateOrderTotalUSD,
  toCheckoutAmounts,
  toPaymentMinorUnits,
} from '../../portal';

const RazorpayPayment = ({
  service,
  user,
  location,
  country,
  fullName,
  address,
  exchangeRate,
  gstin,
  selectedFruit,
  companyType,
  selectedState,
  hasSSN,
  registeredAgent,
  additionalPrice,
  hours,
  promoCodeDetails,
  formationConfig,
  stateFees,
  applyReferralCredit = false,
  referralCredits = 0,
  renderButton,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    const loadScript = () => {
      return new Promise((resolve) => {
        if (window.Razorpay) {
          resolve(true);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };

    loadScript();
  }, []);

  const getOrderTotals = () =>
    calculateOrderTotalUSD({
      service,
      formationConfig,
      stateFees,
      companyType,
      selectedState,
      hasSSN,
      registeredAgent,
      additionalPrice,
      hours,
      promoCodeDetails,
      applyReferralCredit,
      referralCredits,
    });

  const calculateAmount = () => {
    if (!service) return 0;
    const { totalUsd } = getOrderTotals();
    return toPaymentMinorUnits(totalUsd, { location, exchangeRate });
  };

  const getDisplayAmount = () => {
    if (!service) return '';
    const { totalUsd } = getOrderTotals();
    const amounts = toCheckoutAmounts(totalUsd, { location, exchangeRate });
    return location === 'india' ? `₹${amounts.total}` : `$${parseFloat(amounts.total).toFixed(2)}`;
  };

  const handlePayment = async () => {
    try {
      if (!service || !user?._id || !location || !fullName || !address) {
        throw new Error('Missing required fields');
      }
      if (location === 'international' && !country) {
        throw new Error('Please select your country');
      }
      if (!window.Razorpay) throw new Error('Payment gateway is loading');

      const amount = calculateAmount();
      if (!amount || amount <= 0) {
        throw new Error('Invalid payment amount. Please refresh and try again.');
      }

      const currency = location === 'india' ? 'INR' : 'USD';
      const receipt = `rcpt_${user._id.slice(-6)}_${Date.now().toString().slice(-4)}`;
      const { discount } = getOrderTotals();

      const orderResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payment/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          amount,
          currency,
          receipt,
          notes: {
            price: service.price,
            fullName,
            address,
            location,
            country: location === 'international' ? country : 'India',
            service: service.name,
            userId: user._id,
            serviceId: service._id,
            exchangeRate: location === 'india' ? exchangeRate : undefined,
            gstApplied: location === 'india',
            selectedFruit: location === 'india' ? selectedFruit : undefined,
            gstin: location === 'india' ? gstin : undefined,
            email: user.email,
            phone: user.phone,
            promoCodeId: promoCodeDetails ? promoCodeDetails._id : undefined,
            promoDiscount: discount,
          },
        }),
      });

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        throw new Error(errorData.error || 'Failed to create order');
      }

      const order = await orderResponse.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'BooksComply',
        description: `Payment for ${service.name}`,
        image: '/favicon.png',
        order_id: order.id,
        handler: async function (response) {
          try {
            const verifyResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payment/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                orderId: order.id,
              }),
            });

            const verification = await verifyResponse.json();

            if (!verifyResponse.ok || !verification.success) {
              throw new Error(verification.message || 'Payment verification failed');
            }

            toast.success('Payment Successful!');
            navigate(`/payment-success?payment_id=${response.razorpay_payment_id}&order_id=${response.razorpay_order_id}`);
          } catch (error) {
            toast.error(`Payment verification failed: ${error.message}`);
            navigate('/pricing');
          }
        },
        prefill: {
          name: user.name || fullName,
          email: user.email || '',
          contact: user.phone || '',
        },
        theme: {
          color: '#4a6baf',
          hide_topbar: false,
        },
        modal: {
          ondismiss: () => {
            toast.info('Payment window closed');
          },
          escape: true,
          backdropclose: false,
        },
        notes: {
          service: service.name,
          location,
          country: location === 'international' ? country : 'India',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(`Payment error: ${error.message}`);
      console.error('Payment error:', error);
    }
  };

  if (renderButton) {
    return renderButton(handlePayment);
  }

  return (
    <button
      className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold hover:shadow-lg hover:shadow-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      onClick={handlePayment}
      disabled={!service || !user || (location === 'international' && !country)}
    >
      Proceed to Pay {getDisplayAmount()}
    </button>
  );
};

export default RazorpayPayment;
